import { FC } from 'react';
import { RemitReportingSubscriptionInfo } from 'src/apis/remit-reporting/types';
import { Card } from 'src/components/data-display/card';
import { FormatDate } from 'src/components/data-display/format-date';
import { Stack } from 'src/components/layout/stack';
import { ExpandableText } from 'src/components/text/expandable-text';
import { Heading } from 'src/components/text/heading';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { CustomizedToggle } from 'src/pages/settings/premium-services/customized-toggle';
import ReadOnlyOption from 'src/pages/settings/premium-services/read-only-option';

const RemitSubscriptionCard: FC<{
  remitReportingSubscriptionInfo?: RemitReportingSubscriptionInfo;
}> = ({ remitReportingSubscriptionInfo }) => {
  const { minDesktop } = useBreakpoints();

  const active = remitReportingSubscriptionInfo?.active ?? false;
  const acerChannel =
    remitReportingSubscriptionInfo?.channels.includes('SUBMISSION_TO_ACER') ??
    false;
  const emailChannel =
    remitReportingSubscriptionInfo?.channels.includes('EMAIL') ?? false;
  const issuerEic = remitReportingSubscriptionInfo?.issuerEic;
  const validFrom = remitReportingSubscriptionInfo?.validFrom;
  const email = remitReportingSubscriptionInfo?.email;
  const acerOnly = acerChannel && !emailChannel;
  const emailOnly = !acerChannel && emailChannel && issuerEic === undefined;
  const acerAndEmail = acerChannel && emailChannel && issuerEic === undefined;
  const emailEic = !acerChannel && emailChannel && issuerEic !== undefined;

  return (
    <Card>
      <Stack gap={1} templateColumns={minDesktop ? '4fr 2fr' : '1fr'}>
        <div style={{ order: minDesktop ? 2 : 1, textAlign: 'right' }}>
          <CustomizedToggle isChecked={active} />
        </div>
        <div style={{ order: minDesktop ? 1 : 2 }}>
          <Heading mode="card">REMIT reporting</Heading>
        </div>
      </Stack>
      <Stack gap={2}>
        {active ? (
          <>
            <ExpandableText lines={3}>
              If your company is using the REMIT Reporting Services of PRISMA
              for the Secondary Market, this section will provide you with an
              overview of all ACER Reporting Channels currently active for your
              company. Please note that, in case PRISMA shall not carry out the
              reporting to ACER on your behalf, you are obliged to organise the
              reporting on your own.
            </ExpandableText>
            <ExpandableText lines={2}>
              To change the Reporting Channel, and/or find out your previous
              configuration, please contact our Customer Success Team.{' '}
              <a
                href="https://help.prisma-capacity.eu/support/solutions/articles/36000227907-1-reporting-models"
                target="_blank"
              >
                Learn&nbsp;more
              </a>
              &nbsp;about REMIT Reporting in our Knowledge Base.
            </ExpandableText>
            <ExpandableText lines={2}>
              Please note that if, according to the selected configuration,
              PRISMA is not supposed to carry out the reporting to ACER on your
              behalf, you are obliged to arrange for the subsequent reporting to
              ACER on your own.
            </ExpandableText>
          </>
        ) : (
          <ExpandableText lines={2}>
            To place an order for PRISMA REMIT Transaction Data Reporting
            Services, please follow the steps described in our&nbsp;
            <a
              href="https://prisma-capacity.freshdesk.com/a/solutions/articles/36000260269?portalId=36000048165"
              target="_blank"
            >
              Knowledge&nbsp;Base
            </a>
            .
          </ExpandableText>
        )}
        {active && (
          <Stack gap={2} data-testid="remit-subscription-info">
            <ReadOnlyOption selected={acerOnly}>
              <>
                <span>PRISMA only sends the report to ACER</span>
                {acerOnly && <ServiceActivatedSpan validFrom={validFrom} />}
              </>
            </ReadOnlyOption>
            <ReadOnlyOption selected={emailOnly}>
              <>
                <span>PRISMA only sends the report to you through E-mail</span>
                {emailOnly && (
                  <>
                    <ServiceActivatedSpan validFrom={validFrom} />
                    <EmailSpan email={email} />
                  </>
                )}
              </>
            </ReadOnlyOption>
            <ReadOnlyOption selected={acerAndEmail}>
              <>
                <span>PRISMA sends the report to both ACER and you</span>
                {acerAndEmail && (
                  <>
                    <ServiceActivatedSpan validFrom={validFrom} />
                    <EmailSpan email={email} />
                  </>
                )}
              </>
            </ReadOnlyOption>
            <ReadOnlyOption selected={emailEic}>
              <>
                <span>
                  PRISMA sends you the report with the EIC of your RRM through
                  E-mail
                </span>
                {emailEic && (
                  <>
                    <ServiceActivatedSpan validFrom={validFrom} />
                    <EmailSpan email={email} />
                    <span>EIC of RRM: {issuerEic}</span>
                  </>
                )}
              </>
            </ReadOnlyOption>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

const EmailSpan: FC<{ email?: string }> = ({ email }) => {
  return email ? <span>Email recipient: {email}</span> : null;
};

const ServiceActivatedSpan: FC<{ validFrom?: string }> = ({ validFrom }) => {
  return validFrom ? (
    <span>
      Service activated on <FormatDate value={validFrom} type="date-time" />
    </span>
  ) : null;
};

export default RemitSubscriptionCard;
